/**
 * VeraLok Enterprise Design System — Component Library (VLK*)
 *
 * All components respect the design tokens defined in `src/index.css`.
 * Aesthetic: quiet-luxury institutional. Sharp corners (2–4px). Tabular numerals.
 * Fonts: Cabinet Grotesk (display), Manrope (body), IBM Plex Mono (data).
 *
 * Reference: /app/design_guidelines.json and /app/docs/design-system.md
 */
import React from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import {
  Loader2, Lock, ShieldAlert, ShieldCheck, ChevronDown, Circle, X, Check,
} from "lucide-react";

/* ============================== VLKButton ============================== */

const buttonVariants = {
  primary:
    "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary shadow-none",
  secondary:
    "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border",
  ghost:
    "bg-transparent text-foreground hover:bg-secondary",
  outline:
    "bg-transparent border border-border text-foreground hover:bg-secondary",
  destructive:
    "bg-destructive text-destructive-foreground hover:bg-destructive/90",
  link:
    "bg-transparent text-foreground underline-offset-4 hover:underline p-0 h-auto",
};

const buttonSizes = {
  sm: "h-8 px-3 text-xs",
  md: "h-9 px-4 text-sm",
  lg: "h-10 px-5 text-sm",
  xl: "h-12 px-6 text-base",
  icon: "h-9 w-9 p-0",
};

export const VLKButton = React.forwardRef(function VLKButton(
  { className, variant = "primary", size = "md", loading = false, children, disabled, ...rest }, ref
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-sm font-body font-medium",
        "transition-[background-color,border-color,color,box-shadow] duration-150",
        "disabled:opacity-50 disabled:pointer-events-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
        buttonVariants[variant], buttonSizes[size], className
      )}
      {...rest}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
});

/* ============================== VLKCard ============================== */

export function VLKCard({ className, children, elevated = false, ...rest }) {
  return (
    <div
      className={cn(
        "bg-card text-card-foreground border border-border rounded",
        elevated && "shadow-[0_1px_0_0_hsl(var(--border))]",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function VLKCardHeader({ className, children, ...rest }) {
  return <div className={cn("px-5 py-4 border-b border-border", className)} {...rest}>{children}</div>;
}

export function VLKCardTitle({ className, children, ...rest }) {
  return <h3 className={cn("font-display text-base tracking-tight", className)} {...rest}>{children}</h3>;
}

export function VLKCardDescription({ className, children, ...rest }) {
  return <p className={cn("text-xs text-muted-foreground mt-0.5", className)} {...rest}>{children}</p>;
}

export function VLKCardContent({ className, children, ...rest }) {
  return <div className={cn("p-5", className)} {...rest}>{children}</div>;
}

/* ============================== VLKInput ============================== */

export const VLKInput = React.forwardRef(function VLKInput(
  { className, error, ...rest }, ref
) {
  return (
    <input
      ref={ref}
      className={cn(
        "flex h-9 w-full rounded-sm border bg-background px-3 py-2 text-sm",
        "placeholder:text-muted-foreground disabled:opacity-50",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
        error ? "border-destructive" : "border-border",
        className
      )}
      {...rest}
    />
  );
});

export const VLKTextarea = React.forwardRef(function VLKTextarea(
  { className, error, ...rest }, ref
) {
  return (
    <textarea
      ref={ref}
      className={cn(
        "flex min-h-[80px] w-full rounded-sm border bg-background px-3 py-2 text-sm",
        "placeholder:text-muted-foreground disabled:opacity-50",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        error ? "border-destructive" : "border-border",
        className
      )}
      {...rest}
    />
  );
});

export function VLKLabel({ className, children, ...rest }) {
  return (
    <label className={cn("text-xs font-medium text-muted-foreground uppercase tracking-wider", className)} {...rest}>
      {children}
    </label>
  );
}

/* ============================== VLKSelect (native) ============================== */

export const VLKSelect = React.forwardRef(function VLKSelect({ className, children, ...rest }, ref) {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          "flex h-9 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm",
          "appearance-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
          className
        )}
        {...rest}
      >
        {children}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
    </div>
  );
});

/* ============================== VLKStatusBadge ============================== */

const statusStyles = {
  active:    "bg-success/10 text-success border-success/20",
  locked:    "bg-destructive/10 text-destructive border-destructive/20",
  pending:   "bg-warning/10 text-warning border-warning/20",
  approved:  "bg-success/10 text-success border-success/20",
  rejected:  "bg-destructive/10 text-destructive border-destructive/20",
  suspended: "bg-destructive/10 text-destructive border-destructive/20",
  verified:  "bg-info/10 text-info border-info/20",
  default:   "bg-muted text-muted-foreground border-border",
};

export function VLKStatusBadge({ status, children, className }) {
  const key = String(status || "default").toLowerCase();
  const style = statusStyles[key] || statusStyles.default;
  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider",
      style, className
    )}>
      <span className={cn("h-1.5 w-1.5 rounded-full", {
        "bg-success": ["active", "approved", "verified"].includes(key),
        "bg-destructive": ["locked", "rejected", "suspended"].includes(key),
        "bg-warning": key === "pending",
        "bg-info": key === "verified",
        "bg-muted-foreground": !["active","approved","verified","locked","rejected","suspended","pending"].includes(key),
      })} />
      {children || status}
    </span>
  );
}

/* ============================== VLKRiskMeter ============================== */

const riskColors = {
  low: "bg-risk-low",
  medium: "bg-risk-medium",
  high: "bg-risk-high",
  critical: "bg-risk-critical",
};

export function VLKRiskMeter({ level = "low", className }) {
  const segments = ["low", "medium", "high", "critical"];
  const idx = segments.indexOf(String(level).toLowerCase());
  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      <div className="flex gap-0.5">
        {segments.map((seg, i) => (
          <div key={seg} className={cn(
            "h-1.5 w-4 rounded-[1px]",
            i <= idx ? riskColors[level] : "bg-border"
          )} />
        ))}
      </div>
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{level}</span>
    </div>
  );
}

/* ============================== VLKKPICard ============================== */

export function VLKKPICard({ label, value, delta, icon: Icon, tone = "default", testId }) {
  const toneStyles = {
    default: "text-foreground",
    success: "text-success",
    warning: "text-warning",
    danger:  "text-destructive",
    info:    "text-info",
  };
  return (
    <div data-testid={testId} className="bg-card border border-border rounded p-5 relative overflow-hidden group">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">{label}</div>
          <div className={cn("font-display text-3xl mt-2 tabular", toneStyles[tone])}>{value}</div>
          {delta != null && (
            <div className={cn("text-[11px] mt-1 font-mono tabular",
              delta > 0 ? "text-success" : delta < 0 ? "text-destructive" : "text-muted-foreground")}>
              {delta > 0 ? "+" : ""}{delta}
            </div>
          )}
        </div>
        {Icon && (
          <div className="h-8 w-8 rounded-sm border border-border flex items-center justify-center text-muted-foreground group-hover:text-foreground transition-colors">
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================== VLKDataTable ============================== */

export function VLKDataTable({ columns, rows, empty = "No records", rowKey = (r, i) => i, rowTestId, onRowClick }) {
  return (
    <div className="w-full overflow-hidden border border-border rounded bg-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr>
              {columns.map((c) => (
                <th key={c.key}
                    className={cn(
                      "px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium",
                      c.mono && "font-mono", c.align === "right" && "text-right", c.className
                    )}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-muted-foreground text-sm">{empty}</td></tr>
            ) : rows.map((row, i) => (
              <tr key={rowKey(row, i)}
                  data-testid={rowTestId ? rowTestId(row, i) : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    "border-b border-border last:border-0 hover:bg-secondary/50 transition-colors",
                    onRowClick && "cursor-pointer",
                  )}>
                {columns.map((c) => (
                  <td key={c.key}
                      className={cn(
                        "px-4 py-2.5",
                        c.mono && "font-mono text-xs",
                        c.align === "right" && "text-right",
                        c.className
                      )}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================== VLKSkeletonLoader ============================== */

export function VLKSkeleton({ className }) {
  return <div className={cn("animate-pulse bg-muted rounded-sm", className)} />;
}

/* ============================== VLKEmptyState ============================== */

export function VLKEmptyState({ icon: Icon = Circle, title, description, action }) {
  return (
    <div className="flex flex-col items-start gap-3 border border-dashed border-border rounded p-8 bg-card">
      <div className="h-10 w-10 rounded-sm border border-border flex items-center justify-center">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div>
        <div className="font-display text-base">{title}</div>
        {description && <div className="text-sm text-muted-foreground mt-1 max-w-md">{description}</div>}
      </div>
      {action}
    </div>
  );
}

/* ============================== VLKAlertDialog ============================== */

export function VLKAlertDialog({ open, onOpenChange, title, children, actions, tone = "default" }) {
  if (!open) return null;
  const toneStyles = {
    default:     "border-border",
    destructive: "border-destructive/40",
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/70 backdrop-blur-sm animate-fade-in"
         onClick={() => onOpenChange?.(false)}>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()}
        className={cn("w-full max-w-md bg-card border rounded shadow-2xl", toneStyles[tone])}>
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-4">
          <div className="font-display text-base">{title}</div>
          <button onClick={() => onOpenChange?.(false)} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 text-sm text-muted-foreground">{children}</div>
        {actions && <div className="px-5 py-3 border-t border-border flex justify-end gap-2 bg-muted/30">{actions}</div>}
      </motion.div>
    </div>
  );
}

/* ============================== VLKUserAvatar ============================== */

export function VLKUserAvatar({ name = "", src, size = 32, className, testId }) {
  const initials = String(name || "").split(" ").map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
  return (
    <div
      data-testid={testId}
      className={cn("flex items-center justify-center bg-secondary text-secondary-foreground font-medium text-xs rounded-sm border border-border overflow-hidden", className)}
      style={{ width: size, height: size, fontSize: size / 2.6 }}
    >
      {src ? (
        <img src={src} alt={name} className="h-full w-full object-cover" />
      ) : initials}
    </div>
  );
}

/* ============================== VLKPageHeader ============================== */

export function VLKPageHeader({ title, description, actions, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 border-b border-border mb-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">{title}</h1>
        {description && (
          typeof description === "string"
            ? <p className="text-sm text-muted-foreground mt-1">{description}</p>
            : <div className="text-sm text-muted-foreground mt-1">{description}</div>
        )}
        {children}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/* ============================== VLKTabs ============================== */

export function VLKTabs({ value, onChange, items }) {
  return (
    <div className="border-b border-border flex gap-1">
      {items.map((it) => (
        <button
          key={it.value}
          onClick={() => onChange(it.value)}
          className={cn(
            "px-4 py-2 text-sm font-medium transition-colors relative -mb-px border-b-2",
            value === it.value
              ? "text-foreground border-foreground"
              : "text-muted-foreground border-transparent hover:text-foreground"
          )}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

/* ============================== VLKAuditRow ============================== */

export function VLKAuditRow({ row }) {
  return (
    <tr className="border-b border-border hover:bg-secondary/50 font-mono text-xs">
      <td className="px-4 py-2 whitespace-nowrap">{new Date(row.timestamp).toLocaleString()}</td>
      <td className="px-4 py-2">{row.actor_email}</td>
      <td className="px-4 py-2 text-muted-foreground">{row.role}</td>
      <td className="px-4 py-2 font-medium">{row.action}</td>
      <td className="px-4 py-2 text-muted-foreground">{row.target_id}</td>
      <td className="px-4 py-2"><VLKRiskMeter level={row.risk_level} /></td>
      <td className="px-4 py-2">
        <span className={cn("text-[10px] uppercase",
          row.outcome === "SUCCESS" ? "text-success" : "text-destructive")}>{row.outcome}</span>
      </td>
    </tr>
  );
}

/* ============================== VLKPermissionBanner ============================== */

export function VLKPermissionBanner({ role }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-info/5 border border-info/20 text-xs text-info">
      <ShieldCheck className="h-3.5 w-3.5" />
      <span className="font-mono uppercase tracking-wider">Role: {role}</span>
    </div>
  );
}

/* ============================== VLKToast helper via sonner ============================== */
export { toast } from "sonner";

/* ============================== VLKLoadingOverlay ============================== */

export function VLKLoadingOverlay({ label = "Loading" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm">
      <div className="flex items-center gap-3 bg-card border border-border rounded px-4 py-3">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm font-mono uppercase tracking-wider">{label}</span>
      </div>
    </div>
  );
}
